import CONFIG from '../config';
import logger from './logger';

// Cookie utilities
export const setCookie = (name, value, days = 30) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  // Build cookie string with proper settings
  let cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  
  // Add Secure flag if using HTTPS
  if (window.location.protocol === 'https:') {
    cookieString += ';Secure';
  }
  
  // Set the cookie
  document.cookie = cookieString;
  
  // Debug logging
  console.log(`🍪 Cookie set: ${name} (expires in ${days} days, secure: ${window.location.protocol === 'https:'})`);
};

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const value = c.substring(nameEQ.length, c.length);
      // console.log(`🍪 Cookie retrieved: ${name} = ${value ? 'present' : 'empty'}`);
      return value;
    }
  }
  console.log(`🍪 Cookie not found: ${name}`);
  return null;
};

export const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// Access control utilities
export const hasAuthAccess = () => {
  const accessCookie = getCookie(CONFIG.auth.accessCookieName);
  return CONFIG.features.enableAuthAccess || accessCookie === CONFIG.auth.accessCookieValue;
};

export const enableAuthAccess = () => {
  setCookie(CONFIG.auth.accessCookieName, CONFIG.auth.accessCookieValue, CONFIG.auth.cookieExpireDays);
};

// Debug function to check cookie status
export const debugCookies = () => {
  logger.dev('🍪 Cookie debug info - checking auth status');
};

// Session management
export const getAuthToken = () => {
  return getCookie('studysmith_auth_token');
};

export const setAuthToken = (token) => {
  logger.dev('💾 Setting auth token');
  setCookie('studysmith_auth_token', token, 60); // 60 days for persistent auth token
};

export const getRefreshToken = () => {
  return getCookie('studysmith_refresh_token');
};

export const setRefreshToken = (token) => {
  logger.dev('💾 Setting refresh token');
  setCookie('studysmith_refresh_token', token, 60); // 60 days for persistent refresh token
};

export const clearAuthTokens = () => {
  logger.dev('🧹 Clearing auth tokens');
  deleteCookie('studysmith_auth_token');
  deleteCookie('studysmith_refresh_token');
  logger.dev('✅ Auth tokens cleared');
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};

// API utilities
const getApiUrl = (endpoint) => {
  return `${CONFIG.auth.apiBaseUrl}${endpoint}`;
};

// Token refresh lock to prevent race conditions
let refreshPromise = null;

const makeApiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(getApiUrl(endpoint), config);

    // Handle token refresh if needed
    if (response.status === 401 && token && endpoint !== CONFIG.auth.endpoints.refresh) {
      logger.dev('🔐 401 Unauthorized - attempting token refresh');

      // If a refresh is already in progress, wait for it
      if (refreshPromise) {
        logger.dev('⏳ Waiting for existing refresh to complete...');
        const refreshed = await refreshPromise;
        logger.dev('⏳ Existing refresh completed:', refreshed ? 'success' : 'failed');

        if (refreshed) {
          logger.dev('✅ Using refreshed token, retrying original request');
          // Retry the original request with new token
          config.headers['Authorization'] = `Bearer ${getAuthToken()}`;
          const retryResponse = await fetch(getApiUrl(endpoint), config);
          logger.dev('🔄 Retry response status:', retryResponse.status);
          return retryResponse;
        } else {
          logger.dev('❌ Token refresh failed - logging out user');

          // Refresh failed, redirect to signup
          clearAuthTokens();
          window.location.href = '/signup';
          return null;
        }
      }

      // Start a new refresh
      logger.dev('🔄 Starting new token refresh...');
      refreshPromise = refreshAuthToken().finally(() => {
        // Clear the promise after it completes
        refreshPromise = null;
      });

      const refreshed = await refreshPromise;
      if (refreshed) {
        logger.dev('✅ Token refresh successful, retrying original request');
        // Retry the original request with new token
        config.headers['Authorization'] = `Bearer ${getAuthToken()}`;
        const retryResponse = await fetch(getApiUrl(endpoint), config);
        logger.dev('🔄 Retry response status:', retryResponse.status);
        return retryResponse;
      } else {
        logger.dev('❌ Token refresh failed - logging out user');

        // Refresh failed, redirect to signup
        clearAuthTokens();
        window.location.href = '/signup';
        return null;
      }
    }

    return response;
  } catch (error) {
    logger.error('API request failed:', error.message);
    throw error;
  }
};

// Authentication API calls
export const signUp = async (userData) => {
  const response = await makeApiRequest(CONFIG.auth.endpoints.signup, {
    method: 'POST',
    body: JSON.stringify(userData),
  });

  if (response && response.ok) {
    const data = await response.json();
    if (data.session?.access_token) {
      setAuthToken(data.session.access_token);
      // Schedule proactive token refresh
      if (data.session?.expires_at) {
        scheduleTokenRefresh(data.session.expires_at);
      }
    }
    if (data.session?.refresh_token) {
      setRefreshToken(data.session.refresh_token);
    }
    return { success: true, data };
  } else {
    const errorData = await response?.json();
    return { success: false, error: errorData?.message || 'Signup failed' };
  }
};

export const signIn = async (credentials) => {
  const response = await makeApiRequest(CONFIG.auth.endpoints.signin, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });

  if (response && response.ok) {
    const data = await response.json();
    if (data.session?.access_token) {
      setAuthToken(data.session.access_token);
      // Schedule proactive token refresh
      if (data.session?.expires_at) {
        scheduleTokenRefresh(data.session.expires_at);
      }
    }
    if (data.session?.refresh_token) {
      setRefreshToken(data.session.refresh_token);
    }
    return { success: true, data };
  } else {
    const errorData = await response?.json();
    return { success: false, error: errorData?.message || 'Sign in failed' };
  }
};

export const signOut = async () => {
  logger.dev('🚪 Initiating sign out');

  try {
    await makeApiRequest(CONFIG.auth.endpoints.signout, {
      method: 'POST',
    });
    logger.dev('✅ Server signout successful');
  } catch (error) {
    logger.error('❌ Signout request failed:', error.message);
  } finally {
    clearAuthTokens();
    logger.dev('✅ Sign out completed');
  }
};

export const getCurrentUser = async () => {
  const response = await makeApiRequest(CONFIG.auth.endpoints.user, {
    method: 'GET',
  });

  if (response && response.ok) {
    const data = await response.json();
    return { success: true, user: data };
  } else {
    return { success: false, error: 'Failed to get user data' };
  }
};

export const refreshAuthToken = async () => {
  const refreshToken = getRefreshToken();

  logger.dev('🔄 Starting token refresh');

  if (!refreshToken) {
    logger.dev('❌ No refresh token found - user will be logged out');
    return false;
  }

  try {
    logger.apiCall('POST', CONFIG.auth.endpoints.refresh);

    const response = await fetch(getApiUrl(CONFIG.auth.endpoints.refresh), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    logger.dev('📡 Refresh response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      logger.dev('📡 Refresh response received');

      if (data.session?.access_token) {
        setAuthToken(data.session.access_token);
        // Update refresh token if provided
        if (data.session.refresh_token) {
          setRefreshToken(data.session.refresh_token);
          logger.dev('✅ Both tokens refreshed successfully');
        } else {
          logger.dev('✅ Access token refreshed (refresh token unchanged)');
        }

        // Schedule next refresh (if we have expiry info)
        if (data.session?.expires_at) {
          scheduleTokenRefresh(data.session.expires_at);
        }

        return true;
      } else {
        logger.dev('❌ No access token in refresh response');
      }
    } else {
      logger.dev('❌ Refresh failed - HTTP', response.status);
    }
  } catch (error) {
    logger.error('❌ Token refresh failed:', error.message);
  }

  logger.dev('❌ Token refresh ultimately failed');
  return false;
};

// Proactive token refresh scheduler
let refreshTimer = null;

const scheduleTokenRefresh = (expiresAt) => {
  // Clear any existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  // Parse the expiry time
  const expiryTime = expiresAt * 1000; // Convert to milliseconds
  const now = Date.now();

  // Calculate when to refresh (5 minutes before expiry)
  const refreshBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
  const refreshTime = expiryTime - refreshBuffer;
  const timeUntilRefresh = refreshTime - now;

  logger.dev('⏰ Token refresh scheduled');

  // Only schedule if the refresh time is in the future
  if (timeUntilRefresh > 0) {
    refreshTimer = setTimeout(async () => {
      logger.dev('⏰ Proactive token refresh triggered');

      // Use the same refresh promise mechanism to prevent race conditions
      if (!refreshPromise) {
        refreshPromise = refreshAuthToken().finally(() => {
          refreshPromise = null;
        });
      }

      await refreshPromise;
    }, timeUntilRefresh);
  } else {
    logger.dev('⚠️ Token already expired or expiring soon, refreshing immediately');
    // Token is already expired or expiring very soon, refresh immediately
    if (!refreshPromise) {
      refreshPromise = refreshAuthToken().finally(() => {
        refreshPromise = null;
      });
    }
  }
};

// Parse JWT to get expiry time
const parseJwtExpiry = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload.exp || null;
  } catch (error) {
    logger.error('Failed to parse JWT:', error.message);
    return null;
  }
};

// Initialize proactive refresh on page load if authenticated
if (typeof window !== 'undefined') {
  const token = getAuthToken();
  if (token) {
    const expiry = parseJwtExpiry(token);
    if (expiry) {
      scheduleTokenRefresh(expiry);
    }
  }
}

export const resetPassword = async (email) => {
  const response = await makeApiRequest(CONFIG.auth.endpoints.resetPassword, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

  if (response && response.ok) {
    return { success: true };
  } else {
    const errorData = await response?.json();
    return { success: false, error: errorData?.message || 'Password reset failed' };
  }
};

// OAuth utilities
export const initiateOAuth = async (provider) => {
  try {
    // For Supabase OAuth, we need to construct the URL directly
    const supabaseUrl = CONFIG.auth.supabaseUrl || 'https://cisfmmphawxauuyqrrhc.supabase.co';
    const redirectTo = `${window.location.origin}/signup`;

    // Construct the Supabase OAuth URL
    const oauthUrl = `${supabaseUrl}/auth/v1/authorize?provider=${provider}&redirect_to=${encodeURIComponent(redirectTo)}`;

    logger.dev('🔐 Initiating OAuth with:', provider);
    logger.dev('🔗 OAuth redirect configured');

    // Redirect directly to Supabase OAuth
    window.location.href = oauthUrl;

    return { success: true };
  } catch (error) {
    logger.error('OAuth initiation error:', error.message);
    return { success: false, error: 'Failed to initiate OAuth' };
  }
};

// URL parameter utilities for handling OAuth callbacks
export const getUrlParameter = (name) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export const getHashParameter = (name) => {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  return params.get(name);
};

export const handleOAuthCallback = () => {
  // Supabase returns auth data in the URL hash
  const accessToken = getHashParameter('access_token');
  const refreshToken = getHashParameter('refresh_token');
  const expiresIn = getHashParameter('expires_in');
  const error = getHashParameter('error') || getUrlParameter('error');
  const errorDescription = getHashParameter('error_description') || getUrlParameter('error_description');

  logger.dev('🔐 Processing OAuth callback');

  if (error) {
    logger.dev('❌ OAuth error received');
    return { success: false, error: errorDescription || error };
  }

  if (accessToken) {
    setAuthToken(accessToken);

    if (refreshToken) {
      setRefreshToken(refreshToken);
    }

    // Schedule token refresh if we have expiry info
    if (expiresIn) {
      const expiresAt = Math.floor(Date.now() / 1000) + parseInt(expiresIn);
      scheduleTokenRefresh(expiresAt);
    }

    // Clean up URL hash
    window.location.hash = '';

    logger.dev('✅ OAuth successful, tokens stored');
    return { success: true };
  }

  logger.dev('❌ No token received');
  return { success: false, error: 'No authentication token received' };
};

// Feature flag utilities
export const shouldShowAuth = () => {
  return CONFIG.features.showLoginSignup || hasAuthAccess();
};

export const shouldShowWaitingList = () => {
  return CONFIG.features.showWaitingList && !shouldShowAuth();
}; 